import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import Modal from '../Modals/Modal';
import ConfirmationModal from '../Modals/ConfirmationModal';
import toast from 'react-hot-toast';
import '../Modals/SettingsModal.css';
import './ProjectManagement.css';
import AddProjectModal from './AddProjectModal';

const ProjectManagement = ({ workspaceId, updateProjectsAndJobs }) => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [user, setUser] = useState('');
  const [isAddProjectOpen, setIsAddProjectOpen] = useState(false);
  const [isEditProjectOpen, setIsEditProjectOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [newProjectStatus, setNewProjectStatus] = useState(1);
  const [newProjectCodePrefix, setNewProjectCodePrefix] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [loading, setLoading] = useState(true);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previousProjectName, setPreviousProjectName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [workspaces, setWorkspaces] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedSearchQuery, setArchivedSearchQuery] = useState('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [projectToActivate, setProjectToActivate] = useState(null);
  const [activatingProjectId, setActivatingProjectId] = useState(null);
  const inputRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_BASE_URL;

  const handleKeyDown = useCallback(
    (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        if (isEditProjectOpen && newProjectName && !isSubmitting) {
          handleEditProject();
        }
      }
    },
    [isEditProjectOpen, newProjectName, isSubmitting]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    setUser(storedUser?.id || '');
    fetchWorkspacesById();
    fetchWorkspaces();
    fetchProjects();
  }, []);

  // Fetch archived projects when switching to archived view
  useEffect(() => {
    if (showArchived && user) {
      fetchArchivedProjects();
    }
  }, [showArchived, user]);

  const fetchWorkspacesById = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/Workspace/GetWorkspaceById/${workspaceId}`,
        {
          method: 'GET',
        }
      );
      if (!response.ok) {
        throw new Error('Error fetching workspace data!');
      }
      const data = await response.json();
      setWorkspaceName(data.workspace.name);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching Workspace by ID:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkspaces = async () => {
    try {
      const response = await fetch(`${API_URL}/api/Workspace/GetWorkspaces`, {
        method: 'GET',
      });
      if (!response.ok) {
        throw new Error('Error fetching workspaces data!');
      }
      const data = await response.json();
      setWorkspaces(data.workspaces || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching workspaces:', err);
    }
  };

  const fetchArchivedProjects = async () => {
    if (!user) return;
    setArchivedLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/Project/GetMyProjectsByStatus?userId=${user}&status=3`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch archived projects');
      }
      const data = await response.json();
      // Filter to only show projects from current workspace
      const workspaceArchivedProjects = (data.projects || []).filter(
        (project) => project.workspaceId === parseInt(workspaceId)
      );
      setArchivedProjects(workspaceArchivedProjects);
    } catch (err) {
      console.error('Error fetching archived projects:', err);
      toast.error('Failed to load archived projects');
    } finally {
      setArchivedLoading(false);
    }
  };

  useEffect(() => {
    if (isEditProjectOpen && selectedProject) {
      const project = filteredProjects.find((p) => p.id === parseInt(selectedProject));
      if (project) {
        setNewProjectName(project.name || '');
        setNewProjectStatus(project.status || 1);
        setNewProjectDescription(project.description || '');
        setNewProjectCodePrefix(project.codePrefix || '');
      }
    } else if (!isEditProjectOpen) {
      setNewProjectName('');
      setNewProjectStatus(1);
      setNewProjectDescription('');
      setNewProjectCodePrefix('');
    }
  }, [isEditProjectOpen, selectedProject, filteredProjects]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/Project/GetProjects`, {
        method: 'GET',
      });
      if (response.ok) {
        const data = await response.json();
        const result = data.projects;
        const filterResults = result.filter((res) => res.workspaceId == workspaceId);
        setProjects(result || []);
        setFilteredProjects(filterResults || []);
      } else {
        throw new Error('Error fetching projects data!');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const getWorkspaceNameById = (workspaceId) => {
    const workspace = workspaces.find((w) => w.id === parseInt(workspaceId));
    return workspace ? workspace.name : `Workspace ${workspaceId}`;
  };

  const searchedProjects = useMemo(() => {
    if (!searchQuery.trim()) {
      return filteredProjects;
    }
    const query = searchQuery.toLowerCase().trim();
    return filteredProjects.filter((project) => {
      const projectName = project.name?.toLowerCase() || '';
      const projectDescription = project.description?.toLowerCase() || '';
      const workspaceName = getWorkspaceNameById(project.workspaceId).toLowerCase();
      return (
        projectName.includes(query) ||
        projectDescription.includes(query) ||
        workspaceName.includes(query)
      );
    });
  }, [filteredProjects, searchQuery, workspaces]);

  const filteredArchivedProjects = useMemo(() => {
    if (!archivedSearchQuery.trim()) {
      return archivedProjects;
    }
    const query = archivedSearchQuery.toLowerCase().trim();
    return archivedProjects.filter((project) => {
      const projectName = project.name?.toLowerCase() || '';
      const projectDescription = project.description?.toLowerCase() || '';
      return projectName.includes(query) || projectDescription.includes(query);
    });
  }, [archivedProjects, archivedSearchQuery]);

  useEffect(() => {
    setPreviousProjectName(newProjectName);
  }, [newProjectName]);

  const resetFormStates = () => {
    setNewProjectName('');
    setNewProjectDescription('');
    setNewProjectCodePrefix('');
    setNewProjectStatus(1);
    setSelectedWorkspace('');
    setError(null);
  };

  const handleEditProject = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const finalDescription = newProjectDescription.trim() || newProjectName;
      const response = await fetch(
        `${API_URL}/api/Project/UpdateProject/${selectedProject}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: selectedProject,
            name: newProjectName,
            description: finalDescription,
            status: newProjectStatus,
            codePrefix: newProjectCodePrefix,
          }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update project');
      }
      toast.success('Project Updated Successfully');
      setIsEditProjectOpen(false);
      resetFormStates();
      await fetchProjects();
    } catch (err) {
      setError(err.message);
      toast.error(err.message || 'Failed to update project');
      console.error('Error updating project:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActivateClick = (project) => {
    setProjectToActivate(project);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmActivate = async () => {
    if (!projectToActivate) return;
    setIsConfirmModalOpen(false);
    setActivatingProjectId(projectToActivate.id);
    try {
      const updatePayload = {
        id: projectToActivate.id,
        name: projectToActivate.name,
        description: projectToActivate.description || '',
        status: 1,
        codePrefix: projectToActivate.codePrefix || '',
      };
      const response = await fetch(
        `${API_URL}/api/Project/UpdateProject/${projectToActivate.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatePayload),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to activate project');
      }
      toast.success('Project activated successfully');
      await fetchProjects();
      await fetchArchivedProjects();
    } catch (err) {
      toast.error(err.message || 'Failed to activate project');
      console.error('Error activating project:', err);
    } finally {
      setActivatingProjectId(null);
      setProjectToActivate(null);
    }
  };

  const handleCancelActivate = () => {
    setIsConfirmModalOpen(false);
    setProjectToActivate(null);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleClearArchivedSearch = () => {
    setArchivedSearchQuery('');
  };

  const closeEditModal = () => {
    resetFormStates();
    setIsEditProjectOpen(false);
  };

  const refreshProjects = async () => {
    await fetchProjects();
    if (updateProjectsAndJobs) await updateProjectsAndJobs();
  };

  return (
    <div className="settings-content">
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={handleCancelActivate}
        onConfirm={handleConfirmActivate}
        title="Activate Project"
        message={`Are you sure you want to activate "${projectToActivate?.name}"?`}
        confirmText="Activate"
        cancelText="Cancel"
      />

      <div className="settings-action-buttons">
        <button className="btn-primary" onClick={() => setIsAddProjectOpen(true)}>
          Add Project
        </button>
        <button
          className="btn-secondary"
          onClick={() => setIsEditProjectOpen(true)}
          disabled={!selectedProject && !showArchived}
        >
          Edit Project
        </button>
        <button
          className={`btn ${showArchived ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setShowArchived(!showArchived)}
        >
          {showArchived ? 'Show Active Projects' : 'Archived Projects'}
        </button>
      </div>

      {!showArchived ? (
        /* Active Projects View */
        <div className="settings-lookup-list">
          <div className="project-search-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="project-search-input"
              />
              {searchQuery && (
                <button
                  className="clear-search-btn"
                  onClick={handleClearSearch}
                  title="Clear search"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
            {searchQuery && (
              <div className="search-results-info">
                Found {searchedProjects.length} project(s)
              </div>
            )}
          </div>

          <select
            size="5"
            className="lookup-select"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
          >
            <option value="">Select a Project</option>
            {searchedProjects.length > 0 ? (
              searchedProjects
                .filter((project) => project.status !== 3)
                .map((project) => (
                  <option
                    key={project.id}
                    value={project.id}
                    className={project.status !== 1 ? 'inactive-project' : ''}
                  >
                    {workspaceName} → {project.name}{' '}
                    {project.status !== 1 ? '(Inactive)' : ''}
                  </option>
                ))
            ) : (
              <option value="" disabled>
                {searchQuery ? 'No projects found' : 'No projects available'}
              </option>
            )}
          </select>
        </div>
      ) : (
        /* Archived Projects View */
        <div className="settings-lookup-list">
          <div className="project-search-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search archived projects..."
                value={archivedSearchQuery}
                onChange={(e) => setArchivedSearchQuery(e.target.value)}
                className="project-search-input"
              />
              {archivedSearchQuery && (
                <button
                  className="clear-search-btn"
                  onClick={handleClearArchivedSearch}
                  title="Clear search"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
            {archivedSearchQuery && (
              <div className="search-results-info">
                Found {filteredArchivedProjects.length} archived project(s)
              </div>
            )}
          </div>

          {archivedLoading ? (
            <div className="archived-loading">
              <i className="fas fa-spinner fa-spin"></i> Loading archived projects...
            </div>
          ) : filteredArchivedProjects.length > 0 ? (
            <div className="archived-projects-list">
              {filteredArchivedProjects.map((project) => (
                <div key={project.id} className="archived-project-item">
                  <div className="archived-project-info">
                    <div className="archived-project-name">{project.name}</div>
                    {project.description && (
                      <div className="archived-project-description">
                        {project.description}
                      </div>
                    )}
                    <div className="archived-project-meta">
                      <span>ID: {project.id}</span>
                      {project.code && <span>Code: {project.code}</span>}
                    </div>
                  </div>
                  <button
                    className="activate-button"
                    onClick={() => handleActivateClick(project)}
                    disabled={activatingProjectId === project.id}
                  >
                    {activatingProjectId === project.id ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> Activating...
                      </>
                    ) : (
                      'Activate'
                    )}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-archived-projects">
              {archivedSearchQuery
                ? 'No archived projects match your search'
                : 'No archived projects found'}
            </div>
          )}
        </div>
      )}

      <AddProjectModal
        isOpen={isAddProjectOpen}
        onClose={() => setIsAddProjectOpen(false)}
        workspaceId={workspaceId}
        workspaceName={workspaceName}
        userId={user}
        existingProjects={filteredProjects}
        onAddSuccess={refreshProjects}
      />

      <Modal
        isOpen={isEditProjectOpen}
        onClose={closeEditModal}
        title="Edit Project"
        disableClose={isSubmitting}
      >
        <div className="settings-form">
          <div className="form-group">
            <label>Project Name:</label>
            <input
              type="text"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder="Enter project name"
              autoFocus
              disabled={isSubmitting}
            />
          </div>
          <div className="form-group">
            <label>Status:</label>
            <select
              value={newProjectStatus}
              onChange={(e) => setNewProjectStatus(e.target.value)}
              disabled={isSubmitting}
            >
              <option value="1">Active</option>
              <option value="2">Inactive</option>
              <option value="3">Archive</option>
            </select>
          </div>
          <div className="form-group">
            <label>Project Description:</label>
            <textarea
              value={newProjectDescription}
              onChange={(e) => setNewProjectDescription(e.target.value)}
              placeholder="Enter project description"
              disabled={isSubmitting}
            />
          </div>
          <div className="form-group">
            <label>Code Prefix:</label>
            <input
              type="text"
              value={newProjectCodePrefix}
              onChange={(e) => setNewProjectCodePrefix(e.target.value)}
              placeholder="Enter code prefix (e.g., PROJ)"
              disabled={isSubmitting}
            />
          </div>
          {error && (
            <div className="form-error">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}
          <div className="modal-footer">
            <button
              className="btn-primary"
              onClick={handleEditProject}
              disabled={!newProjectName || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Updating...
                </>
              ) : (
                'OK'
              )}
            </button>
            <button className="btn-close" onClick={closeEditModal} disabled={isSubmitting}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectManagement;

